import React from 'react';
import Input from '../common/Input';
import Select from '../common/Select';
import Button from '../common/Button';
import { DEPARTMENTS, SHIFT_PERIODS, SHIFT_VEHICLES } from '../../utils/constants';
import { getShiftPeriodLabel, getShiftVehicleLabel, formatCalendarDate } from '../../utils/helpers';

const emptyShift = () => ({
  shiftDate: '',
  shiftPeriod: 'day',
  startTime: '08:00',
  endTime: '17:00',
  supervisorEmployeeId: '',
  supervisorName: '',
  supervisorDepartment: '',
  supervisorPhone: '',
  supervisorCount: 1,
  workers: [],
  vehicleKey: '',
  vehicleName: '',
  targoNumber: '',
  cartonCount: ''
});

const positionOf = (employees, employeeId, stored) => {
  if (stored) return stored;
  const emp = (employees || []).find((e) => e.id === employeeId);
  return emp?.department || '';
};

const isXamaaliRole = (department) => {
  const value = String(department || '').toLowerCase().replace(/\s+/g, '');
  return value === 'xamaali' || value === 'xamali';
};

const sameRole = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

/** People boxes besides Xamaali and Supervisor. A new department becomes a new box. */
const peopleBoxTitles = (workers, employees) => {
  const titles = DEPARTMENTS.map((d) => d.value).filter(
    (value) => !['Xamaali', 'Supervisor', 'Baabuur'].includes(value)
  );
  const seen = new Set(titles.map((title) => title.toLowerCase()));
  (workers || []).forEach((w) => {
    const role = positionOf(employees, w.employeeId, w.department);
    if (!role || isXamaaliRole(role)) return;
    const key = role.toLowerCase().replace(/\s+/g, '');
    if (key === 'supervisor' || key === 'baabuur') return;
    if (!seen.has(role.toLowerCase())) {
      titles.push(role);
      seen.add(role.toLowerCase());
    }
  });
  return titles;
};

const workersForRole = (workers, employees, role) =>
  (workers || []).filter((w) => {
    if (!w.employeeName && !w.employeeId) return false;
    const department = positionOf(employees, w.employeeId, w.department);
    if (role === 'Xamaali') return isXamaaliRole(department);
    return sameRole(department, role);
  });

const labelBlue = 'text-[#0D47A1]';
const dataBlack = 'text-gray-900';

const NameBox = ({ name, onRemove, brandBlue }) => (
  <span
    className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-normal ${dataBlack} ${
      brandBlue ? 'border-blue-200 bg-white' : 'border-slate-200 bg-slate-50'
    }`}
  >
    {name}
    {onRemove ? (
      <button
        type="button"
        className="text-slate-400 hover:text-red-700 font-bold leading-none"
        onClick={onRemove}
        aria-label={`Ka saar ${name}`}
      >
        ×
      </button>
    ) : null}
  </span>
);

const NameBoxes = ({ people, onRemove, brandBlue }) => {
  if (!people.length) return <p className="text-sm text-slate-400">—</p>;
  return (
    <div className="flex flex-wrap gap-2">
      {people.map((person) => (
        <NameBox
          key={person.employeeId || person.employeeName}
          name={person.employeeName}
          onRemove={onRemove ? () => onRemove(person.employeeId) : undefined}
          brandBlue={brandBlue}
        />
      ))}
    </div>
  );
};

const SheetPanel = ({ title, children, className = '', brandBlue }) => (
  <section
    className={`rounded-xl border bg-white overflow-hidden ${
      brandBlue ? 'border-blue-200' : 'border-slate-200'
    } ${className}`}
  >
    <div className={`h-1 ${brandBlue ? 'bg-[#0D47A1]' : 'bg-red-700'}`} />
    <h4
      className={`px-4 pt-3 text-[11px] font-bold tracking-[0.16em] uppercase ${
        brandBlue ? labelBlue : 'text-slate-800'
      }`}
    >
      {title}
    </h4>
    <div className="px-4 pb-3 pt-1">{children}</div>
  </section>
);

const SheetLine = ({ label, value, brandBlue }) => (
  <div
    className={`flex items-center justify-between gap-3 border-b py-2 last:border-b-0 ${
      brandBlue ? 'border-blue-100' : 'border-slate-100'
    }`}
  >
    <span className={`text-[11px] uppercase tracking-wide font-semibold ${dataBlack}`}>
      {label}
    </span>
    <span className={`text-sm font-normal text-right ${dataBlack}`}>
      {value || '—'}
    </span>
  </div>
);

const ShiftSheet = ({ shift, employees, defaultDate, brandBlue }) => {
  const xamaali = workersForRole(shift.workers, employees, 'Xamaali');
  const baabuurPeople = workersForRole(shift.workers, employees, 'Baabuur');
  const roleTitles = peopleBoxTitles(shift.workers, employees);
  const vehicle = shift.vehicleName || getShiftVehicleLabel(shift.vehicleKey) || '';
  const cartons =
    shift.cartonCount !== '' && shift.cartonCount != null ? String(shift.cartonCount) : '';

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <SheetPanel title="Shift" brandBlue={brandBlue}>
          <SheetLine brandBlue={brandBlue} label="Taariikh" value={formatCalendarDate(shift.shiftDate || defaultDate) || '—'} />
          <SheetLine brandBlue={brandBlue} label="Nooca" value={getShiftPeriodLabel(shift.shiftPeriod) || '—'} />
          <SheetLine brandBlue={brandBlue} label="Bilow" value={shift.startTime || '—'} />
          <SheetLine brandBlue={brandBlue} label="Dhammaad" value={shift.endTime || '—'} />
        </SheetPanel>
        <SheetPanel title="Supervisor" brandBlue={brandBlue}>
          <SheetLine brandBlue={brandBlue} label="Magaca" value={shift.supervisorName || '—'} />
          <SheetLine brandBlue={brandBlue} label="Telefoon" value={shift.supervisorPhone || '—'} />
          {Number(shift.supervisorCount) > 1 ? (
            <SheetLine brandBlue={brandBlue} label="Tirada" value={String(shift.supervisorCount)} />
          ) : null}
        </SheetPanel>
        <SheetPanel title="Xamaali" brandBlue={brandBlue}>
          <NameBoxes people={xamaali} brandBlue={brandBlue} />
        </SheetPanel>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        <SheetPanel title="Packaging" brandBlue={brandBlue}>
          <SheetLine brandBlue={brandBlue} label="Tirada cartons" value={cartons || '—'} />
        </SheetPanel>
        <SheetPanel title="Baabuur" brandBlue={brandBlue}>
          <SheetLine brandBlue={brandBlue} label="Nooca baabuurka" value={vehicle || '—'} />
          <SheetLine brandBlue={brandBlue} label="Targo number" value={shift.targoNumber || '—'} />
          <div className="pt-2">
            <p className={`text-[11px] uppercase tracking-wide mb-2 font-semibold ${dataBlack}`}>
              Magaca
            </p>
            <NameBoxes people={baabuurPeople} brandBlue={brandBlue} />
          </div>
        </SheetPanel>
        {roleTitles.map((title) => (
          <SheetPanel key={title} title={title} brandBlue={brandBlue}>
            <p className={`text-[11px] uppercase tracking-wide mb-2 font-semibold ${dataBlack}`}>
              Magaca
            </p>
            <NameBoxes people={workersForRole(shift.workers, employees, title)} brandBlue={brandBlue} />
          </SheetPanel>
        ))}
      </div>
    </div>
  );
};

const WorkShiftsEditor = ({
  shifts = [],
  employees = [],
  onChange,
  readOnly = false,
  defaultDate = '',
  brandBlue = false
}) => {
  const updateShift = (index, patch) => {
    const next = shifts.map((s, i) => (i === index ? { ...s, ...patch } : s));
    onChange(next);
  };

  const addShift = () => {
    onChange([...shifts, { ...emptyShift(), shiftDate: defaultDate || '' }]);
  };

  const removeShift = (index) => {
    onChange(shifts.filter((_, i) => i !== index));
  };

  const addWorker = (index, employeeId) => {
    if (!employeeId) return;
    const emp = employees.find((x) => x.id === employeeId);
    if (!emp) return;
    const shift = shifts[index] || {};
    const current = shift.workers || [];
    if (current.some((w) => w.employeeId === employeeId)) return;
    updateShift(index, {
      workers: [
        ...current,
        { employeeId: emp.id, employeeName: emp.name, department: emp.department || '' }
      ]
    });
  };

  const removeWorker = (index, employeeId) => {
    const shift = shifts[index] || {};
    updateShift(index, {
      workers: (shift.workers || []).filter((w) => w.employeeId !== employeeId)
    });
  };

  const employeeOptions = employees.filter((e) => e.isActive !== false).map((e) => ({
    value: e.id,
    label: e.department ? `${e.name} — ${e.department}` : e.name
  }));

  const shiftPeriodOptions = SHIFT_PERIODS.map((p) => ({ value: p.value, label: p.label }));
  const vehicleOptions = SHIFT_VEHICLES.map((v) => ({ value: v.value, label: v.label }));

  if (readOnly) {
    if (!shifts.length) return <p className="text-gray-500 text-sm">Shift lama qorin</p>;
    return (
      <div className="space-y-6">
        {shifts.map((shift, idx) => (
          <div key={idx} className="space-y-3">
            {shifts.length > 1 ? (
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Shift {idx + 1}</p>
            ) : null}
            <ShiftSheet shift={shift} employees={employees} defaultDate={defaultDate} brandBlue={brandBlue} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {shifts.map((shift, index) => {
        const workerIds = new Set((shift.workers || []).map((w) => w.employeeId));
        const activeEmployees = employees.filter((e) => e.isActive !== false);
        const available = activeEmployees.filter(
          (e) => e.id !== shift.supervisorEmployeeId && !workerIds.has(e.id)
        );
        const optionsForRole = (role) =>
          available
            .filter((e) => (role === 'Xamaali' ? isXamaaliRole(e.department) : sameRole(e.department, role)))
            .map((e) => ({ value: e.id, label: e.name }));
        const xamaaliWorkers = workersForRole(shift.workers, employees, 'Xamaali');
        const baabuurWorkers = workersForRole(shift.workers, employees, 'Baabuur');
        const roleTitles = peopleBoxTitles(shift.workers, employees);

        return (
          <div key={index} className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                Shift {index + 1}
              </span>
              <button type="button" onClick={() => removeShift(index)} className="text-red-600 text-sm">
                Tirtir
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              <SheetPanel title="Shift" brandBlue={brandBlue}>
                <Input
                  label="Taariikh"
                  name={`shiftDate-${index}`}
                  type="date"
                  value={shift.shiftDate || defaultDate || ''}
                  onChange={(e) => updateShift(index, { shiftDate: e.target.value })}
                  className="mb-2"
                />
                <Select
                  label="Nooca"
                  name={`shiftPeriod-${index}`}
                  value={shift.shiftPeriod || 'day'}
                  onChange={(e) => updateShift(index, { shiftPeriod: e.target.value })}
                  options={shiftPeriodOptions}
                  className="mb-2"
                />
                <Input
                  label="Bilow"
                  name={`start-${index}`}
                  type="time"
                  value={shift.startTime || '08:00'}
                  onChange={(e) => updateShift(index, { startTime: e.target.value })}
                  className="mb-2"
                />
                <Input
                  label="Dhammaad"
                  name={`end-${index}`}
                  type="time"
                  value={shift.endTime || '17:00'}
                  onChange={(e) => updateShift(index, { endTime: e.target.value })}
                  className="mb-0"
                />
              </SheetPanel>

              <SheetPanel title="Supervisor" brandBlue={brandBlue}>
                <Select
                  label="Magaca"
                  name={`supervisor-${index}`}
                  value={shift.supervisorEmployeeId || ''}
                  onChange={(e) => {
                    const emp = employees.find((x) => x.id === e.target.value);
                    const current = shifts[index] || {};
                    updateShift(index, {
                      supervisorEmployeeId: e.target.value,
                      supervisorName: emp?.name || '',
                      supervisorDepartment: emp?.department || '',
                      supervisorPhone: emp?.phone || '',
                      workers: (current.workers || []).filter((w) => w.employeeId !== e.target.value)
                    });
                  }}
                  options={employeeOptions}
                  placeholder="Dooro supervisor..."
                  className="mb-2"
                />
                <Input
                  label="Telefoon"
                  name={`supervisorPhone-${index}`}
                  type="tel"
                  value={shift.supervisorPhone || ''}
                  onChange={(e) => updateShift(index, { supervisorPhone: e.target.value })}
                  placeholder="+252..."
                  className="mb-2"
                />
                <Input
                  label="Tirada"
                  name={`supervisorCount-${index}`}
                  type="number"
                  min="1"
                  value={shift.supervisorCount ?? 1}
                  onChange={(e) => updateShift(index, { supervisorCount: parseInt(e.target.value, 10) || 1 })}
                  className="mb-0"
                />
              </SheetPanel>

              <SheetPanel title="Xamaali" brandBlue={brandBlue}>
                <Select
                  label="Magacyada"
                  name={`addXamaali-${index}`}
                  value=""
                  onChange={(e) => addWorker(index, e.target.value)}
                  options={optionsForRole('Xamaali')}
                  placeholder="Ku dar Xamaali..."
                  className="mb-2"
                />
                <NameBoxes
                  people={xamaaliWorkers}
                  onRemove={(id) => removeWorker(index, id)}
                  brandBlue={brandBlue}
                />
              </SheetPanel>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              <SheetPanel title="Packaging" brandBlue={brandBlue}>
                <Input
                  label="Tirada cartons"
                  name={`cartons-${index}`}
                  type="number"
                  min="0"
                  value={shift.cartonCount ?? ''}
                  onChange={(e) => updateShift(index, { cartonCount: e.target.value })}
                  placeholder="Tirada cartons..."
                  className="mb-0"
                />
              </SheetPanel>
              <SheetPanel title="Baabuur" brandBlue={brandBlue}>
                <Select
                  label="Nooca baabuurka"
                  name={`vehicle-${index}`}
                  value={shift.vehicleKey || ''}
                  onChange={(e) => {
                    const key = e.target.value;
                    updateShift(index, { vehicleKey: key, vehicleName: getShiftVehicleLabel(key) });
                  }}
                  options={vehicleOptions}
                  placeholder="Dooro baabuur..."
                  className="mb-2"
                />
                <Input
                  label="Targo number"
                  name={`targo-${index}`}
                  value={shift.targoNumber || ''}
                  onChange={(e) => updateShift(index, { targoNumber: e.target.value })}
                  placeholder="Lambarka targo..."
                  className="mb-2"
                />
                <Select
                  label="Magaca"
                  name={`addBaabuur-${index}`}
                  value=""
                  onChange={(e) => addWorker(index, e.target.value)}
                  options={optionsForRole('Baabuur')}
                  placeholder="Ku dar Baabuur..."
                  className="mb-2"
                />
                <NameBoxes
                  people={baabuurWorkers}
                  onRemove={(id) => removeWorker(index, id)}
                  brandBlue={brandBlue}
                />
              </SheetPanel>
              {roleTitles.map((title) => (
                <SheetPanel key={title} title={title} brandBlue={brandBlue}>
                  <Select
                    label="Magaca"
                    name={`add-${title}-${index}`}
                    value=""
                    onChange={(e) => addWorker(index, e.target.value)}
                    options={optionsForRole(title)}
                    placeholder={`Ku dar ${title}...`}
                    className="mb-2"
                  />
                  <NameBoxes
                    people={workersForRole(shift.workers, employees, title)}
                    onRemove={(id) => removeWorker(index, id)}
                    brandBlue={brandBlue}
                  />
                </SheetPanel>
              ))}
            </div>
          </div>
        );
      })}
      <Button type="button" variant="outline" onClick={addShift}>
        + Ku dar shift
      </Button>
    </div>
  );
};

export default WorkShiftsEditor;
